<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h1>Theme V2 - Design Tokens</h1>
        <p>The new way to brand your cashier.</p>
        <v-divider class="mt-4"></v-divider>
      </v-col>

      <v-col v-if="isCashierV2" cols="12">
        <v-alert type="warning" variant="tonal" border="start">
          <div class="text-subtitle-1 font-weight-bold">
            Theme V2 is not available on your current cashier version
          </div>
          <p class="mt-2">
            You are currently running <strong>Cashier V2</strong> (Vue<span
              v-if="cashierVersion"
            >
              - branch: {{ cashierVersion }}</span
            >). Theme V2 with design tokens is only available for
            <strong>Cashier V3</strong> (React).
          </p>
          <p class="mt-2">
            To start using Theme V2, switch your merchant over to Cashier V3.
            Existing configuration parameters, theme overrides and HTML
            templates are backward compatible - upgrading does not require a
            re-integration.
          </p>
          <v-btn
            class="mt-4"
            color="warning"
            variant="flat"
            append-icon="mdi-open-in-new"
            :href="docs.cashierV3"
            target="_blank"
          >
            How to switch to Cashier V3
          </v-btn>
        </v-alert>
      </v-col>

      <v-col cols="12" :class="{ 'theme-v2-disabled': isCashierV2 }">
        <v-alert
          v-if="!isCashierV2"
          type="info"
          variant="tonal"
          border="start"
          class="mb-6"
        >
          Theme V2 is only available for merchants running
          <strong>Cashier V3</strong> (React). If you are still on Cashier V2,
          see the
          <a :href="docs.cashierV3" target="_blank">Cashier V3 upgrade guide</a>
          for how to switch.
        </v-alert>

        <h2>What is Theme V2?</h2>
        <p class="mt-2">
          Theme V2 is a semantic design token system that replaces custom CSS
          as the supported way to restyle the cashier. Instead of overriding
          internal class names - which is fragile and may break between
          releases - you set values on stable, semantic tokens.
        </p>

        <v-row class="mt-4">
          <v-col
            v-for="feature in features"
            :key="feature.title"
            cols="12"
            sm="6"
          >
            <v-card variant="outlined" class="pa-4 fill-height">
              <v-icon class="feature-icon mb-2">{{ feature.icon }}</v-icon>
              <h4>{{ feature.title }}</h4>
              <p class="text-body-2 mt-1">{{ feature.text }}</p>
            </v-card>
          </v-col>
        </v-row>

        <h2 class="mt-8">Try it out</h2>
        <p class="mt-2">
          Play around with Theme V2 tokens and seed colors in the interactive
          demo page, and see the result on a live cashier right away.
        </p>
        <v-btn
          class="mt-4 theme-main-bg text-white"
          variant="flat"
          size="large"
          append-icon="mdi-open-in-new"
          :disabled="isCashierV2"
          :href="demoUrl"
          target="_blank"
        >
          Open the Theme V2 demo
        </v-btn>

        <h2 class="mt-8">Documentation</h2>
        <v-list class="mt-2" density="compact">
          <v-list-item
            v-for="link in docLinks"
            :key="link.title"
            :href="link.url"
            target="_blank"
            link
          >
            <template v-slot:prepend>
              <v-icon>mdi-book-open-variant</v-icon>
            </template>
            <v-list-item-title>{{ link.title }}</v-list-item-title>
            <v-list-item-subtitle>{{ link.subtitle }}</v-list-item-subtitle>
            <template v-slot:append>
              <v-icon size="small">mdi-open-in-new</v-icon>
            </template>
          </v-list-item>
        </v-list>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'

// Docs paths are joined onto the environment-specific docs host (see docsBase).
const DOC_PATHS = {
  cashierV3:
    '/docs/developers/integration_guides/cashier/00-guide-cashier-v3',
  themeV2:
    '/docs/developers/integration_guides/cashier/03b-guide-theming-with-design-tokens'
}

export default {
  name: 'ThemeV2Info',
  data: () => ({
    features: [
      {
        icon: 'mdi-palette',
        title: 'Seed colors',
        text: 'Supply a couple of brand colors and the cashier generates a full, perceptually balanced color palette automatically.'
      },
      {
        icon: 'mdi-theme-light-dark',
        title: 'Dark mode',
        text: "Every token supports light and dark values. Set themeMode to 'auto' and the cashier follows the player's prefers-color-scheme."
      },
      {
        icon: 'mdi-tune',
        title: 'Per-section granularity',
        text: 'Fine-tune individual UI sections such as buttons, tabs, inputs and cards through semantic token paths.'
      },
      {
        icon: 'mdi-shield-check',
        title: 'Stable API',
        text: 'Tokens are a supported contract that will not break between cashier releases, unlike custom CSS targeting internal class names.'
      }
    ]
  }),
  computed: {
    ...mapState({
      config: ({ context }) => context.config,
      cashierVersion: ({ context }) => context.cashierVersion,
      cashierMajorVersion: ({ context }) => context.cashierMajorVersion
    }),
    isCashierV2() {
      return this.cashierMajorVersion === 'v2'
    },
    // Production cashier -> production docs, anything else -> staging docs.
    docsBase() {
      return this.config.environment === 'production'
        ? 'https://docs.paymentiq.io'
        : 'https://test-docs.paymentiq.io'
    },
    docs() {
      return {
        cashierV3: `${this.docsBase}${DOC_PATHS.cashierV3}`,
        themeV2: `${this.docsBase}${DOC_PATHS.themeV2}`
      }
    },
    demoUrl() {
      const environment = this.config.environment || 'test'
      const merchantId = this.config.merchantId || '3636'
      return `https://checkout.paymentiq.io/cashier/v3/staging/demo-theme-v2?environment=${environment}&merchantId=${merchantId}`
    },
    docLinks() {
      return [
        {
          title: 'Theming with Design Tokens (v2)',
          subtitle: 'Token structure, seed colors and v1 to v2 migration',
          url: this.docs.themeV2
        },
        {
          title: "Cashier V3: What's New & How to Upgrade",
          subtitle: 'How to switch between Cashier V2 and V3',
          url: this.docs.cashierV3
        }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
.theme-v2-disabled {
  opacity: 0.5;
  pointer-events: none;
}
.feature-icon {
  color: #247656;
}
</style>
